import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { _Api } from "../../api/_call";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pagination } from "../../common/Pagination";
import { transactionTotals, buildTodayDateRange, buildLastMonthDateRange, buildStatusDateMapFromState } from "../../common/helpers/function";
import { formatDate } from "../../common/helpers/formatDate";
import Select, { components } from "react-select";
import { error, loading, Updateloading } from "../../common/helpers/toastify";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import TableLoader from "../../../admin/common/TableLoader";
import { Stockadjustmentdetails } from "../subTables/StockAdjustment/stockadjustmentdetails";
import ProductModal from "../../common/modules/ProductModal";
import ProductImageSliderModal from "../../common/modules/productImageslider";
import ConfirmationModel from "../../common/modules/confirmationmodel";

/* -----------------------------------------------------------------------------
 * Stock Adjustment — listing page.
 * Design mirrors stock_transfer.js. API endpoints below are PLACEHOLDERS —
 * swap them for the real Stock Adjustment endpoints once available:
 *   list   : api/v1/Inventory/stockadjustment/getStockAdjustmentList
 *   export : api/v1/Inventory/stockadjustment/getStockAdjustmentExport
 * ---------------------------------------------------------------------------*/
const LIST_ENDPOINT = "api/v1/Inventory/stockAdjustment/list";
const EXPORT_ENDPOINT =
  "api/v1/Inventory/stockAdjustment/getStockAdjustmentExport";
// PLACEHOLDER — change-status endpoint (swap when the real one is available)
const STATUS_ENDPOINT = "api/v1/Inventory/stockAdjustment/changeStatus";
const DETAILS_ENDPOINT = "api/v1/Inventory/stockAdjustment/getSADetails";

// Allowed forward transition from each status.
//   pending  -> approved
//   approved -> reverted
//   reverted -> (terminal, no change)
const NEXT_STATUS = {
  pending: "approved",
  approved: "reverted",
};

// Colored status-change dropdown — same look/behavior as the PO list
// (list.js): a solid pill showing the current status, opening a popup of
// colored option buttons for the allowed next status.
const colorStyles = {
  option: (styles, { data }) => ({
    ...styles,
    backgroundColor: data.color,
    color: "#fff",
    fontSize: "16px",
    padding: "12px 16px",
    fontFamily: "inherit",
    cursor: "pointer",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  control: (base) => ({
    ...base,
    fontSize: "14px",
    fontFamily: "inherit",
    minHeight: "40px",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
    marginTop: "8px",
    padding: "6px 12px",
    position: "relative",
  }),
};

const CustomMenu = (props) => (
  <components.Menu {...props}>
    <div
      style={{
        position: "absolute",
        top: "-10px",
        left: "calc(100% - 75px)",
        width: 0,
        height: 0,
        borderLeft: "12px solid transparent",
        borderRight: "12px solid transparent",
        borderBottom: "16px solid #fff",
      }}
    />
    {props.children}
  </components.Menu>
);

// Table columns for custom (client-side) sorting — no jQuery DataTables.
const HEAD_COLUMNS = [
  { key: "date", label: "Date", sortable: true },
  { key: "adj_no", label: "SA No.", sortable: true },
  { key: "location", label: "Location", sortable: true },
  { key: "reason", label: "Reason", sortable: true },
  { key: "sku", label: "SKU", sortable: true },
  { key: "qty", label: "Qty", sortable: true },
  { key: "amount", label: "Amount", sortable: true },
  { key: "status", label: "Status", sortable: true },
  { key: "agent", label: "Agent", sortable: true },
  { key: "remark", label: "Remark", sortable: false },
  { key: "created", label: "Created", sortable: true },
  { key: "modified", label: "Modified", sortable: true },
  { key: "pdf", label: "PDF", sortable: false },
];

// Value used for sorting each sortable column.
const SORT_ACCESSORS = {
  date: (r) => r?.date,
  adj_no: (r) => r?.adj_no,
  location: (r) => r?.location,
  reason: (r) => r?.reason,
  sku: (r) => Number(r?.sku_count) || 0,
  qty: (r) => Number(r?.qty) || 0,
  amount: (r) => Number(r?.amount_value) || 0,
  status: (r) => r?.status,
  agent: (r) => r?.agent,
  created: (r) => r?.created,
  modified: (r) => r?.modified,
};

const StockAdjustment = () => {
  const { t } = useTranslation();
  const dataFetched = useRef();
  const base_url_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const parmissiondatashow = useSelector((state) => state.app.ApplyParmission);
  const totalcountsnumber = useSelector((state) => state.app.totalcountsnumber);
  const [limit, setLimit] = useState(100);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [skip] = useState(0);
  const [filtername, setFiltername] = useState("");
  const [datalodaer, setDatsLodaer] = useState(false);
  const [adjustmentlist, setAdjustmentlist] = useState([]);
  const [Productmodaldata, setProductmodaldata] = useState();
  const [imageid, setImageid] = useState();
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [statement, setStatement] = useState("");
  const [datefilter, setDatefilter] = useState(() => buildLastMonthDateRange().filter);
  const [datelable, setDatelable] = useState("LastMonth");
  const [filterchangestatus, setfilterchangestatus] = useState("pending");
  const datePickerRef = useRef(null);

  const [statusDates, setStatusDates] = useState(() => {
    const lastMonth = buildLastMonthDateRange();
    const today = buildTodayDateRange();
    return {
      pending: lastMonth,
      approved: lastMonth,
      reverted: today,
    };
  });

  const applyStatusDateToUi = (statusKey) => {
    const next = statusDates[statusKey] || buildLastMonthDateRange();
    setDatefilter([new Date(next.filter[0]), new Date(next.filter[1])]);
    setDatelable(next.label);
  };

  const updateCurrentStatusDate = (filter, label) => {
    setStatusDates((prev) => ({
      ...prev,
      [filterchangestatus]: { filter, label },
    }));
    setDatefilter(filter);
    setDatelable(label);
  };

  const [state, setstate] = useState({ totalPages: 20, currentPage: 1 });
  const { totalPages, currentPage } = state;

  const selectOptions = [
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 },
  ];

  // Custom client-side sorting (replaces jQuery DataTables sorting).
  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedList = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortConfig.key];
    if (!accessor) return adjustmentlist;
    const sorted = [...adjustmentlist].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [adjustmentlist, sortConfig]);

  // Status filter tabs — `value` is the string the list API expects.
  const filter_status = [
    { label: t("Pending"), labelcount: "pending", value: "pending", color: "yellow" },
    { label: t("Approved"), labelcount: "approved", value: "approved", color: "main-green" },
    { label: t("Reverted"), labelcount: "reverted", value: "reverted", color: "danger" },
  ];
  // Maps the status-filter bar's color names to real CSS colors, so the
  // active top-level tab's underline matches whichever status is selected.
  const STATUS_COLOR_HEX = {
    yellow: "#F9BC44",
    "main-green": "#5CC7C1",
    danger: "#FF5757",
  };
  const activeTabUnderlineColor =
    STATUS_COLOR_HEX[
      filter_status.find((f) => f.value === filterchangestatus)?.color
    ] || "#5CC7C1";

  const changeDates = (lable) => {
    const MainDate = new Date();
    if (lable === "Today") {
      const s = new Date();
      s.setHours(0, 0, 0, 0);
      updateCurrentStatusDate([s, new Date()], "Today");
    } else if (lable === "Yesterday") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 1));
      s.setHours(0, 0, 0, 0);
      const e = new Date(s);
      e.setHours(23, 59, 59, 999);
      updateCurrentStatusDate([s, e], "Yesterday");
    } else if (lable === "7days") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 7));
      s.setHours(0, 0, 0, 0);
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      updateCurrentStatusDate([s, e], "7days");
    } else if (lable === "30days") {
      const s = new Date(MainDate.setDate(MainDate.getDate() - 30));
      s.setHours(0, 0, 0, 0);
      const e = new Date();
      e.setHours(23, 59, 59, 999);
      updateCurrentStatusDate([s, e], "30days");
    } else if (lable === "ThisMonth") {
      const s = new Date(MainDate.getFullYear(), MainDate.getMonth(), 1);
      s.setHours(0, 0, 0, 0);
      updateCurrentStatusDate([s, new Date()], "ThisMonth");
    } else if (lable === "LastMonth") {
      const s = new Date(MainDate.getFullYear(), MainDate.getMonth() - 1, 1);
      s.setHours(0, 0, 0, 0);
      const e = new Date(MainDate.getFullYear(), MainDate.getMonth(), 0);
      e.setHours(23, 59, 59, 999);
      updateCurrentStatusDate([s, e], "LastMonth");
    }
  };

  const GetStockAdjustment = useCallback(
    async (current, firstlimit, filters_status, search) => {
      setDatsLodaer(true);
      try {
        setTimeout(async function () {
          const postdata = {
            status: filters_status ? filters_status : filterchangestatus,
            search: search ? search : "",
            limit: firstlimit ? firstlimit : limit,
            skip: skip === 0 ? skip : current ? (current - 1) * limit : 0,
            start_date: datefilter ? datefilter[0] : "",
            end_date: datefilter ? datefilter[1] : "",
          };
          let res = await _Api(postdata, LIST_ENDPOINT);
          if (res?.code === 200) {
            transactionTotals({
              start_date: datefilter[0],
              end_date: datefilter[1],
              statusDateMap: buildStatusDateMapFromState(statusDates),
              moduleKey: "SA",
            });
            setAdjustmentlist(res?.data || []);
            setstate((prev) => ({
              ...prev,
              totalPages: Math.ceil((res?.count || 0) / postdata.limit),
              currentPage: current ? current : 1,
            }));
            setDatsLodaer(false);
            dataFetched.current = false;
          } else {
            setAdjustmentlist([]);
            setDatsLodaer(false);
          }
        }, 100);
      } catch (err) {
        console.log(err, "err");
        setDatsLodaer(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datefilter, limit, skip, filterchangestatus, statusDates]
  );

  useEffect(() => {
    if (dataFetched.current) return;
    dataFetched.current = true;
    GetStockAdjustment();
  }, [GetStockAdjustment]);

  const handlePaginations = (current) => {
    setstate({ ...state, currentPage: current });
    GetStockAdjustment(current, "", filterchangestatus, "");
  };

  const filterbyname = (search) => {
    setstate({ ...state, currentPage: 1 });
    GetStockAdjustment(currentPage, limit, filterchangestatus, search);
  };

  const getlimit = (value) => {
    setLimit(value);
    GetStockAdjustment(1, value, filterchangestatus);
    setstate({ ...state, currentPage: 1 });
  };

  const filterbystatus = (statusValue) => {
    setfilterchangestatus(statusValue);
    setstate({ ...state, currentPage: 1 });
    applyStatusDateToUi(statusValue);
    dataFetched.current = false;
  };

  const STATUS_META = {
    pending: { label: t("Pending"), cls: "status-pending", color: "#F9BC44" },
    approved: {
      label: t("Approved"),
      cls: "status-approved",
      color: "#0ECB81",
    },
    reverted: { label: t("Reverted"), cls: "status-canceled", color: "#FF5757" },
  };

  // The only status a row can move to, based on its current status.
  const nextStatusOptions = (status) => {
    const next = NEXT_STATUS[status];
    return next
      ? [
          {
            label: STATUS_META[next]?.label,
            value: next,
            color: STATUS_META[next]?.color,
          },
        ]
      : [];
  };

  // Ask for confirmation before actually calling the change-status API.
  const requestStatusChange = (id, newStatus) => {
    if (!id || !newStatus) return;
    setPendingStatusChange({ id, newStatus });
    setStatement(t("Change Status"));
    document.querySelector("#status_query_select").click();
  };

  const changeStatus = async () => {
    const id = pendingStatusChange?.id;
    const newStatus = pendingStatusChange?.newStatus;
    if (!id || !newStatus) return;
    let GetloadingID = loading();
    try {
      const postdata = { adjustment_id: id, status: newStatus };
      let res = await _Api(postdata, STATUS_ENDPOINT);
      if (res?.code === 200) {
        Updateloading(t, GetloadingID, t("Status updated"));
        GetStockAdjustment(currentPage, limit, filterchangestatus);
      } else {
        Updateloading(t, GetloadingID, res?.message, "error");
      }
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    } finally {
      setPendingStatusChange(null);
    }
  };

  // Expand / collapse a row's line-item sub-table (rendered inline by React).
  const setSubTable = async (id) => {
    const target = adjustmentlist.find((item) => item.adjustment_id === id);
    if (target?.subTableData) {
      // collapse
      setAdjustmentlist((prev) =>
        prev.map((item) =>
          item.adjustment_id === id ? { ...item, subTableData: null } : item
        )
      );
      return;
    }
    try {
      const details = await _Api({ adjustment_id: id }, DETAILS_ENDPOINT);
      if (details?.code === 200) {
        setAdjustmentlist((prev) =>
          prev.map((item) =>
            item.adjustment_id === id
              ? { ...item, subTableData: details }
              : item
          )
        );
      } else {
        error(details?.message || t("Something went wrong"));
      }
    } catch (err) {
      console.log(err, "err");
    }
  };

  const downloadexclsheet = async () => {
    let GetloadingID = loading();
    try {
      const postdata = {
        search: filtername ? filtername : "",
        start_date: datefilter ? datefilter[0] : "",
        end_date: datefilter ? datefilter[1] : "",
      };
      let res = await _Api(postdata, EXPORT_ENDPOINT);
      if (res?.code === 200) {
        const rows = res?.data || [];
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = {
          Sheets: { StockAdjustment: worksheet },
          SheetNames: ["StockAdjustment"],
        };
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const data = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });
        FileSaver.saveAs(data, "StockAdjustment.xlsx");
        Updateloading(t, GetloadingID, t("Excel Export Successfully!!"));
      } else {
        Updateloading(t, GetloadingID, res?.message || t("Something went wrong"), "error");
      }
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    }
  };

  return (
    <React.Fragment>
      <div className="main-body">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h1 className="main-body-heading">{t("Transaction")}</h1>
              <div className="main-body-top-tab-bar">
                <div
                  className="main-body-top-tab-bar-left-col nav nav-pills"
                  role="tablist"
                >
                  <Link
                    to="/app/inventory"
                    className={
                      parmissiondatashow?.purchase_order?.View
                        ? "main-body-top-tab-bar-left-col-a nav-link"
                        : "d-none"
                    }
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      {t("Purchase Order")}
                    </span>
                    <span className="num-tab">
                      {totalcountsnumber?.PO?.total
                        ? totalcountsnumber?.PO?.total
                        : "0"}
                    </span>
                  </Link>
                  <Link
                    to="/app/inventory/transactionPurchase"
                    className={
                      parmissiondatashow?.inventory_transaction_purchase?.View
                        ? "main-body-top-tab-bar-left-col-a nav-link"
                        : "d-none"
                    }
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      {t("Purchase")}
                    </span>
                    <span className="num-tab">
                      {totalcountsnumber?.PU?.total
                        ? totalcountsnumber?.PU?.total
                        : "0"}
                    </span>
                  </Link>
                  <Link
                    className={
                      parmissiondatashow?.inventory_transaction_stock_transfer
                        ?.View
                        ? "main-body-top-tab-bar-left-col-a nav-link"
                        : "d-none"
                    }
                    to="/app/inventory/stock_transfer"
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      {t("Stock Transfer")}
                    </span>
                    <span className="num-tab">
                      {totalcountsnumber?.ST?.total
                        ? totalcountsnumber?.ST?.total
                        : "0"}
                    </span>
                  </Link>
                  <Link
                    className={
                      parmissiondatashow?.inventory_transaction_stock_Receive
                        ?.View
                        ? "main-body-top-tab-bar-left-col-a nav-link"
                        : "d-none"
                    }
                    to="/app/inventory/transactionStockReceive"
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      {t("Stock Receive")}
                    </span>
                    <span className="num-tab">
                      {totalcountsnumber?.SR ? totalcountsnumber?.SR : "0"}
                    </span>
                  </Link>
                  <Link
                    className="main-body-top-tab-bar-left-col-a nav-link active"
                    to="/app/inventory/stockAdjustment"
                    style={{ "--tab-underline-color": activeTabUnderlineColor }}
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      {t("Stock Adjustment")}
                    </span>
                    <span className="num-tab">
                      {totalcountsnumber?.SA?.total
                        ? totalcountsnumber?.SA?.total
                        : "0"}
                    </span>
                  </Link>
                </div>
                <div className="main-body-top-tab-bar-right-col">
                  <Link
                    to="/app/inventory/createSAInventory"
                    className="main-body-top-tab-bar-right-col-a"
                  >
                    <img
                      src={base_url_assets + "images/icons/plus-circle-white.png"}
                      alt=""
                    />
                    <span className="main-body-top-tab-bar-right-col-a-text">
                      {t("Create")}
                    </span>
                  </Link>
                </div>
              </div>

              <div className="main-body-top-color-bar">
                {filter_status.map((result, key) => {
                  return (
                    <div
                      className={
                        "main-body-top-color-bar-single" +
                        (filterchangestatus === result?.value ? " active" : "")
                      }
                      key={key}
                      onClick={() => filterbystatus(result?.value)}
                    >
                      <div
                        className={
                          "main-body-top-color-bar-single-color " +
                          result?.color +
                          "-bg"
                        }
                      />
                      <span
                        className={
                          "main-body-top-color-bar-single-color-status " +
                          result?.color +
                          "-text"
                        }
                      >
                        {result?.label}
                      </span>
                      <span>
                        {totalcountsnumber?.SA?.[result?.labelcount]
                          ? totalcountsnumber?.SA?.[result?.labelcount]
                          : "0"}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="main-body-top-status-bar">
                <Select
                  className=" select-set select p-0 main-content-wrapper-body-top-status-bar-select-num"
                  options={selectOptions}
                  value={{ label: limit, value: limit }}
                  onChange={(e) => {
                    setLimit(e.value);
                    getlimit(e.value);
                  }}
                  isSearchable={false}
                />
                <div className="main-body-top-status-bar-filter">
                  <input
                    type="text"
                    value={filtername}
                    className="main-body-top-status-bar-filter-input"
                    placeholder={t("SA No./StockID")}
                    onKeyPress={(e) =>
                      e.key === "Enter" && filterbyname(e.target.value)
                    }
                    onChange={(e) => setFiltername(e.target.value)}
                  />
                  {filtername ? (
                    <img
                      className="main-body-top-status-bar-filter-clear"
                      src={base_assets + "images/icons/False.png"}
                      onClick={() => {
                        setFiltername("");
                        filterbyname("");
                      }}
                      alt=""
                    />
                  ) : (
                    ""
                  )}
                  <button
                    onClick={() => filterbyname(filtername)}
                    className="main-body-top-status-bar-filter-search"
                  >
                    <i className="fa fa-search" aria-hidden="true" />
                  </button>
                </div>
                <div className="main-body-top-status-bar-multi-date-picker">
                  <div className="main-body-top-status-bar-note-button common-dropdown replenish-date">
                    <button
                      className="dropdown-toggle btn-style align-items-center d-flex"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 4.66667V8L10.5 10.5M15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8Z"
                          stroke="currentcolor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t(datelable)}
                    </button>
                    <ul
                      className="dropdown-menu dropdown-menu-right-fix"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      <li
                        className={`dropdown-item ${datelable === "Today" ? "active" : ""}`}
                        onClick={() => changeDates("Today")}
                      >
                        {t("Today")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "Yesterday" ? "active" : ""}`}
                        onClick={() => changeDates("Yesterday")}
                      >
                        {t("Yesterday")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "7days" ? "active" : ""}`}
                        onClick={() => changeDates("7days")}
                      >
                        {t("Last 7 days")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "30days" ? "active" : ""}`}
                        onClick={() => changeDates("30days")}
                      >
                        {t("Last 30 days")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "ThisMonth" ? "active" : ""}`}
                        onClick={() => changeDates("ThisMonth")}
                      >
                        {t("This Month")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "LastMonth" ? "active" : ""}`}
                        onClick={() => changeDates("LastMonth")}
                      >
                        {t("Last Month")}
                      </li>
                      <li
                        className={`dropdown-item ${datelable === "Custom Range" ? "active" : ""}`}
                        onClick={() => datePickerRef.current.setOpen(true)}
                      >
                        {t("Custom Range")}
                      </li>
                    </ul>
                    <DatePicker
                      selectsRange={true}
                      monthsShown={2}
                      startDate={datefilter[0]}
                      endDate={datefilter[1]}
                      onChange={(update) => {
                        const startDate = update[0];
                        const endDate = update[1];
                        setDatelable("Custom Range");
                        if (startDate && endDate) {
                          const s = new Date(startDate);
                          s.setHours(0, 0, 0, 0);
                          const e = new Date(endDate);
                          e.setHours(23, 59, 59, 999);
                          updateCurrentStatusDate([s, e], "Custom Range");
                        } else {
                          setDatefilter([startDate, endDate]);
                        }
                      }}
                      dateFormat="yyyy-MM-dd"
                      ref={datePickerRef}
                      autoComplete="none"
                    />
                  </div>
                  <div className="main-body-top-status-bar-print-export-dropdown dropdown">
                    <button
                      className="btn dropdown-toggle"
                      type="button"
                      id="dropdownMenuButton1"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <img
                        src={base_url_assets + "images/icons/ellipsis-circular.png"}
                        alt=""
                      />
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      <li
                        className="dropdown-item"
                        onClick={() => downloadexclsheet()}
                      >
                        <div className="dropdown-item-img">
                          <img
                            src={base_url_assets + "images/icons/export-x-icon.png"}
                            alt=""
                          />
                        </div>
                        <span>{t("export")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div
                className="main-body-main-table-wrap position-relative"
                id="table-scroll"
              >
                <table
                  id="stock_adjustment_table"
                  className="common-table table-striped main-table w-100"
                >
                  <thead>
                    <tr>
                      {HEAD_COLUMNS.map((col) => {
                        const active = sortConfig.key === col.key;
                        return (
                          <th
                            key={col.key}
                            className={col.sortable ? "cursor-pointer" : ""}
                            style={
                              col.key === "status" ? { width: "110px" } : undefined
                            }
                            onClick={
                              col.sortable ? () => handleSort(col.key) : undefined
                            }
                          >
                            {t(col.label)}
                            {col.sortable ? (
                              <div className="up-down-arrow-btn">
                                <img
                                  className={
                                    "up-down-arrow-img up-down-arrow-img-forward" +
                                    (active && sortConfig.direction === "desc"
                                      ? ""
                                      : " active")
                                  }
                                  src={
                                    base_url_assets +
                                    "images/icons/up-down-arrow.png"
                                  }
                                  alt=""
                                />
                                <img
                                  className={
                                    "up-down-arrow-img up-down-arrow-img-reverse" +
                                    (active && sortConfig.direction === "desc"
                                      ? " active"
                                      : "")
                                  }
                                  src={
                                    base_url_assets +
                                    "images/icons/up-down-arrow-reverse.png"
                                  }
                                  alt=""
                                />
                              </div>
                            ) : null}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedList?.length
                      ? sortedList.map((result, key) => (
                          <React.Fragment key={result?.adjustment_id || key}>
                          <tr id={`common-main-table-wrap-${result?.adjustment_id}`}>
                            <td className="fixed-side">
                              {result?.date ? formatDate(result?.date) : ""}
                            </td>
                            <td className="po-no">
                              {result?.status === "pending" ? (
                                <Link
                                  to="/app/inventory/createStockAdjustment"
                                  state={{
                                    adjustmentid: result?.adjustment_id,
                                  }}
                                >
                                  {result?.adj_no ? result?.adj_no : ""}
                                </Link>
                              ) : (
                                <span>
                                  {result?.adj_no ? result?.adj_no : ""}
                                </span>
                              )}
                            </td>
                            <td>{result?.location ? result?.location : ""}</td>
                            <td>{result?.reason ? result?.reason : ""}</td>
                            <td
                              className="sku-col table_btn_expand_col"
                              id={"table-btn_" + result?.adjustment_id}
                              onClick={() => setSubTable(result?.adjustment_id)}
                            >
                              <div className="td-icons-wrap">
                                <div>
                                  <img
                                    className={
                                      "sku-col-arrow cursor-pointer" +
                                      (result?.subTableData ? " rotate-90" : "")
                                    }
                                    src={
                                      base_url_assets +
                                      "images/icons/right_green_button.png"
                                    }
                                    alt=""
                                  />
                                  <span>
                                    {result?.sku_count ? result?.sku_count : "0"}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>{result?.qty ? result?.qty : "0"}</td>
                            <td>{result?.amount ? result?.amount : ""}</td>
                            <td className="col-highlight">
                              <div
                                className={
                                  "dropdown status-dropdown status-dropdown-select " +
                                  (STATUS_META[result?.status]?.cls || "")
                                }
                              >
                                <Select
                                  options={nextStatusOptions(result?.status)}
                                  value={{
                                    label:
                                      STATUS_META[result?.status]?.label ||
                                      result?.status,
                                    value: result?.status,
                                    color: STATUS_META[result?.status]?.color,
                                  }}
                                  isDisabled={
                                    !nextStatusOptions(result?.status).length
                                  }
                                  onChange={(event) =>
                                    requestStatusChange(
                                      result?.adjustment_id,
                                      event.value
                                    )
                                  }
                                  classNamePrefix="status-dropdown-select"
                                  styles={colorStyles}
                                  getOptionLabel={(e) => (
                                    <div className="">{e.label}</div>
                                  )}
                                  isSearchable={false}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  components={{ Menu: CustomMenu }}
                                />
                              </div>
                            </td>
                            <td>{result?.agent ? result?.agent : ""}</td>
                            <td>{result?.remark ? result?.remark : ""}</td>
                            <td>
                              {result?.created ? formatDate(result?.created) : ""}
                            </td>
                            <td>
                              {result?.modified
                                ? formatDate(result?.modified)
                                : ""}
                            </td>
                            <td />
                          </tr>
                          {result?.subTableData
                            ? Stockadjustmentdetails(
                                result.subTableData,
                                setImageid,
                                setProductmodaldata,
                                t
                              )
                            : null}
                          </React.Fragment>
                        ))
                      : !datalodaer && (
                          <tr className="bg-white">
                            <td
                              colSpan={HEAD_COLUMNS.length}
                              className="text-center bg-white"
                            >
                              {t("No Data Available")}
                            </td>
                          </tr>
                        )}
                  </tbody>
                </table>
                {datalodaer && <TableLoader />}
              </div>
              {sortedList.length > 0 ? (
                <Pagination
                  total={totalPages}
                  current={currentPage}
                  pagination={(crPage) => handlePaginations(crPage)}
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
      <ProductModal
        data_product={Productmodaldata}
        setOneproduct={setProductmodaldata}
        editproduct_submit={""}
      />
      <ProductImageSliderModal main_id={imageid}></ProductImageSliderModal>
      <ConfirmationModel
        statement={statement}
        handler_submit={changeStatus}
      ></ConfirmationModel>
      <button
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#createPurchaseModal"
        id="status_query_select"
      ></button>
    </React.Fragment>
  );
};

export default StockAdjustment;
